
import { CalendarEvent } from "@/hooks/calendar/types";
import { CalendarSettings } from "@/lib/supabase";
import { useTimeSlotAvailability } from "./timeSlotHooks/useTimeSlotAvailability";
import { useTimeSlotEvents } from "./timeSlotHooks/useTimeSlotEvents";
import { useTimeSlotDialogs } from "./timeSlotHooks/useTimeSlotDialogs";
import { useTimeSlotHandlers } from "./timeSlotHooks/useTimeSlotHandlers";
import { useTimeSlotSelection } from "./timeSlotHooks/useTimeSlotSelection";
import { TimeSlotHookProps } from "./timeSlotHooks/types";

export const useTimeSlotManagement = (
  events: CalendarEvent[] = [],
  settings: CalendarSettings,
  onAddEvent?: (event: Omit<CalendarEvent, "id">) => Promise<void>,
  onDeleteEvent?: (event: CalendarEvent) => Promise<void>,
  isClientView: boolean = false,
  clientId: string | null = null
) => {
  // Hook per verifica disponibilità slot
  const { isTimeSlotUnavailable, isTimeSlotInPast } = useTimeSlotAvailability(settings);
  
  // Hook per gestione eventi negli slot
  const { getEventsForDateAndHour } = useTimeSlotEvents(events);
  
  // Hook per gestione dialoghi
  const {
    selectedTimeSlot,
    setSelectedTimeSlot,
    conflictingEvents,
    setConflictingEvents,
    eventToDelete,
    setEventToDelete,
    dialogState,
    setIsBookingDialogOpen,
    setIsClientRequestDialogOpen,
    setIsConflictDialogOpen,
    setIsPastTimeSlotDialogOpen,
    setDeleteDialogOpen
  } = useTimeSlotDialogs();
  
  // Hook per selezione time slot
  const { handleTimeSlotClick } = useTimeSlotSelection(
    setSelectedTimeSlot,
    getEventsForDateAndHour,
    setConflictingEvents,
    setIsConflictDialogOpen,
    setIsPastTimeSlotDialogOpen,
    setIsBookingDialogOpen,
    setIsClientRequestDialogOpen,
    isTimeSlotInPast,
    isClientView,
    onAddEvent
  );
  
  // Hook per gestione handler - ho modificato l'ordine dei parametri
  const {
    handlePastTimeSlotConfirm,
    handlePastTimeSlotCancel,
    handleConflictConfirm,
    handleConflictCancel,
    handleDeleteEvent,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleBookingClose,
    handleBookingConfirm,
    handleClientRequestConfirm
  } = useTimeSlotHandlers(
    selectedTimeSlot,
    setSelectedTimeSlot,
    setIsBookingDialogOpen,
    setIsClientRequestDialogOpen,
    setIsConflictDialogOpen,
    setIsPastTimeSlotDialogOpen,
    setDeleteDialogOpen,
    setEventToDelete,
    setConflictingEvents,
    isClientView,
    clientId,
    getEventsForDateAndHour,
    onAddEvent,
    onDeleteEvent
  );

  // Ritorniamo tutti gli elementi necessari
  return {
    selectedTimeSlot,
    isBookingDialogOpen: dialogState.isBookingDialogOpen,
    isClientRequestDialogOpen: dialogState.isClientRequestDialogOpen,
    isConflictDialogOpen: dialogState.isConflictDialogOpen,
    isPastTimeSlotDialogOpen: dialogState.isPastTimeSlotDialogOpen,
    conflictingEvents,
    deleteDialogOpen: dialogState.deleteDialogOpen,
    eventToDelete,
    isTimeSlotUnavailable,
    isTimeSlotInPast,
    getEventsForDateAndHour,
    handleTimeSlotClick,
    handlePastTimeSlotConfirm,
    handlePastTimeSlotCancel,
    handleConflictConfirm,
    handleConflictCancel,
    handleDeleteEvent,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleBookingClose,
    handleBookingConfirm,
    handleClientRequestConfirm,
    setIsBookingDialogOpen,
    setIsClientRequestDialogOpen,
    setIsConflictDialogOpen,
    setIsPastTimeSlotDialogOpen,
    setDeleteDialogOpen,
    setEventToDelete
  };
};
